import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, ChevronDown } from "lucide-react";
import { useGetAdminStatisticsQuery } from "../../slices/attendanceApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import "../../pages/css/Feedback.css";

export default function AdminAttendance() {
  const navigate = useNavigate();
  const academySelectRef = useRef(null);
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [showAcademyList, setShowAcademyList] = useState(false);

  // Fetch academies for the dropdown filter
  const { data: academiesData } = useGetAcademyQuery();
  const academies = academiesData || [];

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  // Fetch admin statistics for dashboard
  const { data: statsData, isLoading: statsLoading } =
    useGetAdminStatisticsQuery({ date: today });

  const handleNavigateToRecords = (path) => {
    navigate(path);
  };

  // Get academy name by ID
  const getAcademyName = (academyId) => {
    if (!academyId || !academies.length) return "N/A";
    const academy = academies.find(
      (acad) => acad.academyId === academyId || acad._id === academyId
    );
    return academy?.name || "N/A";
  };

  // Circular progress component for dashboard
  const CircularProgress = ({
    percentage,
    label,
    subtitle,
    present,
    total,
    roleName,
  }) => {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="w-full h-full">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-zinc-800 mb-1">{label}</h3>
          <p className="text-sm text-zinc-600 mb-4">{subtitle}</p>
        </div>
        <div className="flex items-center justify-between w-full">
          {/* Circular Progress on Left */}
          <div className="relative w-32 h-32 flex-shrink-0">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r={radius}
                stroke="#e5e7eb"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="64"
                cy="64"
                r={radius}
                stroke="#8b5cf6"
                strokeWidth="8"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className="transition-all duration-300"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-semibold text-zinc-800">
                {percentage}%
              </span>
            </div>
          </div>

          {/* Text Content on Right */}
          <div className="flex-1 flex items-center justify-center w-full">
            <div className="w-fit">
              <span className="text-4xl font-bold text-zinc-800">
                {present}
              </span>
              <p className="text-sm text-zinc-500 mt-1">
                / {total} {roleName}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get stats based on selected academy or overall
  const stats = useMemo(() => {
    if (!statsData) return null;
    if (selectedAcademyId !== "all" && statsData.academyStats) {
      const academyStat = statsData.academyStats.find(
        (stat) => stat.academyId === selectedAcademyId
      );
      // Return null if academy is selected but not found in academyStats
      return academyStat || null;
    }
    return statsData.overallStats;
  }, [statsData, selectedAcademyId]);

  const managerPercentage =
    stats && stats.totalManagers > 0
      ? Math.round((stats.presentManagers / stats.totalManagers) * 100)
      : 0;
  const supervisorPercentage =
    stats && stats.totalSupervisors > 0
      ? Math.round((stats.presentSupervisors / stats.totalSupervisors) * 100)
      : 0;
  const technicianPercentage =
    stats && stats.totalTechnicians > 0
      ? Math.round((stats.presentTechnicians / stats.totalTechnicians) * 100)
      : 0;
  const cfmPercentage =
    stats && stats.totalCfms > 0
      ? Math.round((stats.presentCfms / stats.totalCfms) * 100)
      : 0;

  const academyName =
    selectedAcademyId !== "all"
      ? getAcademyName(selectedAcademyId)
      : "All Academies";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        academySelectRef.current &&
        !academySelectRef.current.contains(event.target)
      ) {
        setShowAcademyList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="bg-white py-6 px-8 h-full space-y-8">
      {/* Quick Links Section */}
      <div className="max-w-4xl">
        <h2 className="text-2xl font-bold text-zinc-800 mb-2">Quick Links</h2>
        <p className="text-zinc-600 mb-6">
          Check your employee leave requests and attendance
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Employee Attendance Card */}
          <button
            onClick={() =>
              handleNavigateToRecords("/admin/attendance/employee-attendance")
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#B9E5DD] p-3 rounded-lg">
                <User className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Employee Attendance
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Check your employee's attendance
                </p>
              </div>
            </div>
          </button>

          {/* Employee Leave Requests Card */}
          <button
            onClick={() =>
              handleNavigateToRecords(
                "/admin/attendance/employee-leave-requests"
              )
            }
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#B9E5DD] p-3 rounded-lg">
                <User className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Employee Leave Requests
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Check your subordinates leave request
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Today's Overview Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-zinc-800 mb-2">
              {academyName}
            </h2>
            <p className="text-zinc-600">Today's Overview</p>
          </div>
          {/* Custom Academy Filter - Minimal Shadcn Style */}
          <div ref={academySelectRef} className="relative min-w-[200px]">
            <div
              className="flex h-9 w-full items-center justify-between rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm shadow-sm transition-colors hover:bg-zinc-50 focus:outline-none focus:ring-1 focus:ring-zinc-950 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setShowAcademyList(!showAcademyList)}
            >
              <span className="text-zinc-700">
                {selectedAcademyId === "all"
                  ? "Select an Academy"
                  : getAcademyName(selectedAcademyId)}
              </span>
              <ChevronDown
                className="h-4 w-4 text-zinc-500 opacity-50"
                style={{
                  transform: showAcademyList
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 200ms ease-in-out",
                }}
              />
            </div>
            {showAcademyList && (
              <div className="absolute left-0 top-[calc(100%+4px)] z-50 min-w-[200px] rounded-md border border-zinc-200 bg-white p-1 text-zinc-950 shadow-md">
                <div
                  className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${
                    selectedAcademyId === "all"
                      ? "bg-zinc-100 text-zinc-900"
                      : "hover:bg-zinc-100"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAcademyId("all");
                    setShowAcademyList(false);
                  }}
                >
                  All Academies
                </div>
                {academies.map((academy) => (
                  <div
                    key={academy.academyId || academy._id}
                    className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${
                      selectedAcademyId === (academy.academyId || academy._id)
                        ? "bg-zinc-100 text-zinc-900"
                        : "hover:bg-zinc-100"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAcademyId(academy.academyId || academy._id);
                      setShowAcademyList(false);
                    }}
                  >
                    {academy.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {statsLoading ? (
          <div className="bg-white rounded-lg border p-16 text-center">
            <p className="text-xl font-semibold text-zinc-800 mb-2">
              Loading...
            </p>
          </div>
        ) : !stats ? (
          <div className="bg-white rounded-lg border p-16 text-center">
            <p className="text-xl font-semibold text-zinc-800 mb-2">
              {selectedAcademyId !== "all"
                ? `No data available for ${academyName}`
                : "No data available"}
            </p>
            {selectedAcademyId !== "all" && (
              <p className="text-sm text-zinc-500 mt-2">
                This academy may not have any users assigned yet.
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.totalCfms > 0 && (
              <div className="bg-white rounded-lg border p-6">
                <CircularProgress
                  percentage={cfmPercentage}
                  label="CFM"
                  subtitle="Attendance for today"
                  present={stats.presentCfms}
                  total={stats.totalCfms}
                  roleName="cfms"
                />
              </div>
            )}
            <div className="bg-white rounded-lg border p-6">
              <CircularProgress
                percentage={managerPercentage}
                label="Manager"
                subtitle="Attendance for today"
                present={stats.presentManagers}
                total={stats.totalManagers}
                roleName="managers"
              />
            </div>
            <div className="bg-white rounded-lg border p-6">
              <CircularProgress
                percentage={supervisorPercentage}
                label="Supervisor"
                subtitle="Attendance for today"
                present={stats.presentSupervisors}
                total={stats.totalSupervisors}
                roleName="supervisors"
              />
            </div>
            <div className="bg-white rounded-lg border p-6">
              <CircularProgress
                percentage={technicianPercentage}
                label="Technicians"
                subtitle="Attendance for today"
                present={stats.presentTechnicians}
                total={stats.totalTechnicians}
                roleName="technicians"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
